// Turning what Bailey asks to show into the real thing, with Beu's own tools
// and the salon's own facts: a social post made the way Marketing › Social
// makes one (free times from the diary, reviews word for word, prices from
// the menu), and an email in the salon's brand from its own templates.
import { parseRequest, createFromRequest } from '../../../marketing-v2/features/social/data/ask'
import { applyEdit } from '../../../marketing-v2/features/social/data/askAI'
import { withStylist } from '../../../marketing-v2/features/social/data/askData'
import { buildCaption } from '../../../marketing-v2/features/social/data/captions'
import { POST_TYPES } from '../../../marketing-v2/features/social/data/postTypes'
import { emailFor, emailPayload, templateFor } from '../../../marketing-v2/features/plans/data/emailFor'
import { emailTemplateFor } from './templates'
import { textLinks } from '../../../marketing-v2/features/plans/data/sms'
import { withOptOut } from '../../../marketing-v2/features/sms-composer/smsUtils'

const drop = (o) => Object.keys(o).reduce((a, k) => (o[k] === undefined || o[k] === null || o[k] === '' ? a : { ...a, [k]: o[k] }), {})

// A post from a post block: Social's own understanding of the words (and the
// salon's records it needs), then Bailey's words and choices on top, checked
// the way the studio checks them (no number that isn't in the facts).
// `gather` is Social's useGatherAsk. Returns { post, changes, needs, notes,
// captionText }.
export const buildPostFrom = async (block, { gather, kit, trade = 'hair', salonName = '', town = '', now = new Date() } = {}) => {
  const kind = block.kind && POST_TYPES[block.kind] ? block.kind : null
  const req = parseRequest(block.ask || (kind ? POST_TYPES[kind].label : ''), { now })
  if (kind) req.job = kind
  if (block.format) req.format = block.format
  // A post planned for later isn't a request to post it now
  req.schedule = null
  req.shareNow = false
  let data = {}
  try { data = (gather && (await gather(req, null))) || {} } catch (e) { data = {} }
  const captionFor = (p, voice) => buildCaption(p, { voice, salon: salonName, town })
  const ctx = { ...data, now, kit, trade, captionFor, text: block.ask || '' }
  const start = createFromRequest(req, ctx)
  let res = { post: start.post, changes: start.changes || [], needs: start.needs || [], notes: start.notes || [] }
  const edit = drop({ words: block.words, format: block.format, postAt: block.when, style: block.style, layout: block.layout, colourway: block.colourway })
  if (Object.keys(edit).length) {
    const e = applyEdit(res.post, edit, { ...ctx, fresh: true })
    res = { post: e.post, changes: [...res.changes, ...(e.changes || [])], needs: [...res.needs, ...(e.needs || [])], notes: [...res.notes, ...(e.notes || [])] }
  }
  const post = { ...withStylist(res.post, ctx), fromBailey: true }
  // The words that go with it, as the studio would write them (the studio
  // writes them again when it opens, in the voice the salon picks)
  let captionText = ''
  try { captionText = (post.caption && post.caption.text) || captionFor(post, (post.caption && post.caption.voice) || 'warm').text || '' } catch (e) { captionText = '' }
  return { ...res, post, captionText }
}

// The email a block describes, in the template Bailey named (or the one that
// fits what they asked), or the salon's own template for its purpose
export const emailFromBlock = (block, market = 'hair', asked = '') => ({
  templateId: emailTemplateFor(block, { market, asked }) || templateFor(block.purpose || 'moment', market),
  subject: block.subject || block.heading || '',
  preheader: block.preheader || '',
  hero: drop({
    headline: block.heading || block.subject || '',
    body: (block.body || []).join('\n\n'),
    button: block.button || 'Book now',
    buttonUrl: '{{BookingLink}}',
  }),
})

// { blocks, subject, preheader, html } to show, with a sample client's name
export const drawEmail = (block, { market = 'hair', kit, links, salonName = '', asked = '' } = {}) => emailFor(emailFromBlock(block, market, asked), { market, kit, links, salonName })

// What the campaign API is sent for a draft: the email creator's own draft
// shape. Never scheduled, never sent: the salon picks who gets it and sends
// it from Campaigns.
export const emailDraft = (block, { market = 'hair', kit, links, salonName = '', replyTo = '', asked = '' } = {}) => ({
  name: block.title || block.subject || block.heading || 'Email from Bailey',
  ...emailPayload(emailFromBlock(block, market, asked), { market, kit, links, salonName }),
  ...(salonName ? { senderName: salonName } : {}),
  ...(replyTo ? { replyToEmail: replyTo } : {}),
  audienceSegment: 'custom',
  clientIds: [],
  status: 'draft',
})

// What the campaign API is sent for a text Bailey wrote: a draft, with the
// salon's real links in place of {{GiftCardLink}} and the like, and the
// opt-out line it will go with. Never scheduled, never sent.
export const textDraft = (block, { links } = {}) => ({
  name: block.title || 'Text from Bailey',
  type: 'sms',
  message: withOptOut(textLinks(block.message || '', links || {})),
  audienceSegment: 'custom',
  clientIds: [],
  status: 'draft',
})
